/**
 * Task Management Helper Utilities
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TASK_STATUS, COMPLETED_STATUSES, ACTIVE_STATUSES } from './taskConstants';

dayjs.extend(relativeTime);

/**
 * Normalize a task object from the API to handle field name mismatches.
 * Backend sends taskAssignedUsers but frontend expects assignedUsers.
 * Backend sends isOverDue (capital D) but frontend expects isOverdue.
 */
export function normalizeTask<T extends Record<string, any>>(task: T): T {
  if (!task) return task;
  const normalized = { ...task } as any;
  // assignedUsers alias
  if (!normalized.assignedUsers && normalized.taskAssignedUsers) {
    normalized.assignedUsers = normalized.taskAssignedUsers;
  }
  // loopedInUsers alias (mirrors assignedUsers)
  if (!normalized.loopedInUsers && normalized.taskLoopedInUsers) {
    normalized.loopedInUsers = normalized.taskLoopedInUsers;
  }
  // isOverdue alias
  if (normalized.isOverdue === undefined && normalized.isOverDue !== undefined) {
    normalized.isOverdue = normalized.isOverDue;
  }
  return normalized as T;
}

/**
 * Return true when the given user ID is in the task's active looped-in list.
 */
export function isLoopedInUser(
  task: Record<string, any> | null | undefined,
  userId?: string | null,
): boolean {
  if (!task || !userId) return false;
  const list = task.loopedInUsers || task.taskLoopedInUsers || [];
  return list.some(
    (u: any) => !u?.isRemoved && getAssignedUserId(u) === userId,
  );
}

/**
 * Return true when the given user is actively assigned to the task (not removed).
 */
export function isAssigneeUser(
  task: Record<string, any> | null | undefined,
  userId?: string | null,
): boolean {
  if (!task || !userId) return false;
  const list = task.assignedUsers || task.taskAssignedUsers || [];
  return list.some(
    (u: any) => !u?.isRemoved && getAssignedUserId(u) === userId,
  );
}

/**
 * Normalize an array of tasks
 */
export function normalizeTasks<T extends Record<string, any>>(tasks: T[]): T[] {
  return tasks.map(normalizeTask);
}

/**
 * Get the display name for an assigned user.
 * Backend sends firstName + surname, NOT displayName.
 */
export function getAssignedUserName(user: Record<string, any>): string {
  // Try displayName first (legacy/computed)
  if (user.displayName) return user.displayName;
  // Build from firstName + surname (what the API actually sends)
  const parts = [user.firstName, user.surname].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  // Fall back to email
  if (user.email) return user.email;
  return 'Unknown';
}

/**
 * Get the user's initials from an assigned user
 */
export function getAssignedUserInitials(user: Record<string, any>): string {
  if (user.firstName && user.surname) {
    return (user.firstName[0] + user.surname[0]).toUpperCase();
  }
  if (user.firstName) return user.firstName[0].toUpperCase();
  if (user.displayName) return user.displayName[0].toUpperCase();
  if (user.email) return user.email[0].toUpperCase();
  return '?';
}

/**
 * Get user ID from an assigned user (handles both userId and userID)
 */
export function getAssignedUserId(user: Record<string, any>): string {
  return user.userId || user.userID || '';
}

/**
 * Check if a task is a self-task (created by and assigned to the same user)
 */
export function isSelfTask(
  task: Record<string, any>,
  currentUserId?: string
): boolean {
  if (!currentUserId || !task.createdBy) return false;
  if (task.createdBy !== currentUserId) return false;

  // Handle both field names from API
  const assignedUsers = task.assignedUsers || task.taskAssignedUsers || [];
  if (assignedUsers.length === 0) return false;

  return assignedUsers.some(
    (u: any) => getAssignedUserId(u) === currentUserId
  );
}

/**
 * A "real" date, not a placeholder default. Treat anything before 1950
 * as unset — covers .NET `DateTime.MinValue` (0001-01-01), SQL
 * `smalldatetime` default (1900-01-01), and their TZ-adjusted cousins
 * that sometimes surface as "1 Jan 1901" in the UI.
 */
function isPlaceholderDate(d: dayjs.Dayjs): boolean {
  return d.year() < 1950;
}

/**
 * Format a CCMS decimal timestamp (yyyyMMddHHmmss) to a readable date
 */
export function formatCcmsTimestamp(timestamp?: number | null, format = 'MMM D, YYYY'): string {
  if (!timestamp) return 'N/A';
  const str = timestamp.toString();
  if (str.length < 8) return 'N/A';

  const year = str.substring(0, 4);
  const month = str.substring(4, 6);
  const day = str.substring(6, 8);
  const hour = str.length >= 10 ? str.substring(8, 10) : '00';
  const minute = str.length >= 12 ? str.substring(10, 12) : '00';
  const second = str.length >= 14 ? str.substring(12, 14) : '00';

  const date = dayjs(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  if (!date.isValid() || isPlaceholderDate(date)) return 'N/A';
  return date.format(format);
}

/**
 * Format a date string or Date to display format
 */
export function formatDate(date?: string | Date | null, format = 'MMM D, YYYY'): string {
  if (!date) return 'N/A';
  const d = dayjs(date);
  if (!d.isValid() || isPlaceholderDate(d)) return 'N/A';
  return d.format(format);
}

/**
 * Format a date to relative time (e.g., "2 days ago", "in 3 hours")
 */
export function formatRelativeDate(date?: string | Date | null): string {
  if (!date) return 'N/A';
  const d = dayjs(date);
  if (!d.isValid() || isPlaceholderDate(d)) return 'N/A';
  return d.fromNow();
}

/**
 * Get days until due date (negative means overdue)
 */
export function getDaysUntilDue(dueDate?: string | Date | null): number | null {
  if (!dueDate) return null;
  const due = dayjs(dueDate);
  if (!due.isValid()) return null;
  return due.diff(dayjs(), 'day');
}

/**
 * Check if a task is overdue
 */
export function isTaskOverdue(task: {
  dueDate?: string | Date | null;
  statusID?: number;
  isOverdue?: boolean;
}): boolean {
  if (task.isOverdue !== undefined) return task.isOverdue;
  if (!task.dueDate) return false;
  if (COMPLETED_STATUSES.includes(task.statusID as any)) return false;
  return dayjs(task.dueDate).isBefore(dayjs(), 'day');
}

/**
 * Check if task status is active (user is working on it)
 */
export function isActiveStatus(statusId: number): boolean {
  return ACTIVE_STATUSES.includes(statusId as any);
}

/**
 * Check if task status is completed
 */
export function isCompletedStatus(statusId: number): boolean {
  return COMPLETED_STATUSES.includes(statusId as any);
}

/**
 * Check if task is unseen
 */
export function isUnseenTask(statusId: number): boolean {
  return statusId === TASK_STATUS.UNSEEN;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Strip HTML tags from a string (for previews)
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

/**
 * Check whether a rich-text editor value is effectively empty.
 * Rich-text editors often emit markup like `<p><br></p>` or `<p>&nbsp;</p>`
 * for an empty field, so a plain `.trim()` check is not enough.
 */
export function isEmptyHtml(html: string | null | undefined): boolean {
  if (!html) return true;
  const withoutTags = html.replace(/<[^>]*>/g, '');
  const withoutEntities = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&amp;nbsp;/gi, ' ');
  return withoutEntities.trim().length === 0;
}

/**
 * Calculate a priority score for AI prioritizer
 * Higher score = more urgent/important
 */
export function calculatePriorityScore(task: {
  dueDate?: string | Date | null;
  statusID?: number;
  priorityID?: number;
  isOverdue?: boolean;
}): number {
  let score = 0;

  // Overdue: highest weight
  if (isTaskOverdue(task)) {
    score += 100;
    const daysOverdue = Math.abs(getDaysUntilDue(task.dueDate) || 0);
    score += Math.min(daysOverdue * 5, 50); // Up to 50 extra for days overdue
  }

  // Due date proximity
  const daysUntilDue = getDaysUntilDue(task.dueDate);
  if (daysUntilDue !== null && daysUntilDue >= 0) {
    if (daysUntilDue <= 1) score += 80;
    else if (daysUntilDue <= 3) score += 60;
    else if (daysUntilDue <= 7) score += 40;
    else if (daysUntilDue <= 14) score += 20;
    else score += 10;
  }

  // Priority weight
  const priorityId = task.priorityID ?? 0;
  score += priorityId * 15; // High(2)=30, Medium(1)=15, Low(0)=0

  // Status weight (unseen tasks get a bump)
  if (task.statusID === TASK_STATUS.UNSEEN) score += 10;
  else if (task.statusID === TASK_STATUS.SEEN_IT) score += 5;

  return score;
}

/**
 * Group tasks by a key extractor function
 */
export function groupTasksBy<T>(
  tasks: T[],
  keyFn: (task: T) => string | number
): Record<string | number, T[]> {
  return tasks.reduce(
    (groups, task) => {
      const key = keyFn(task);
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
      return groups;
    },
    {} as Record<string | number, T[]>
  );
}

/**
 * Sort tasks by due date (earliest first, null dates last)
 */
export function sortByDueDate<T extends { dueDate?: string | Date | null }>(
  tasks: T[],
  ascending = true
): T[] {
  return [...tasks].sort((a, b) => {
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    const diff = dayjs(a.dueDate).diff(dayjs(b.dueDate));
    return ascending ? diff : -diff;
  });
}
